import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaPlus } from "react-icons/fa";
import KnownLearningToggle from "./KnownLearningToggle";
import SearchBar from "./SearchBar";
import WordsPhrasesTabs, { ContentType } from "./WordsPhrasesTabs";

type ViewMode = "known" | "learning";

interface SegmentedViewHeaderProps {
  contentType: ContentType;
  onContentTypeChange: (type: ContentType) => void;
  viewMode: ViewMode;
  knownCount: number;
  learningCount: number;
  searchQuery: string;
  onViewModeChange: (mode: ViewMode) => void;
  onSearchChange: (value: string) => void;
  /** When omitted, the "+" add button is hidden (iOS SegmentedViewHeader only
   *  renders it when onAddPress is provided). */
  onAddPress?: () => void;
  searchPlaceholder?: string;
}

const SegmentedViewHeader: React.FC<SegmentedViewHeaderProps> = ({
  contentType,
  onContentTypeChange,
  viewMode,
  knownCount,
  learningCount,
  searchQuery,
  onViewModeChange,
  onSearchChange,
  onAddPress,
  searchPlaceholder = "Search...",
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const handleSearchToggle = () => {
    if (searchOpen && searchQuery) {
      onSearchChange("");
    }
    setSearchOpen((o) => !o);
  };

  return (
    <>
      {/* Words / Phrases segmented tabs */}
      <WordsPhrasesTabs value={contentType} onChange={onContentTypeChange} />

      {/* Toggle + search + add row */}
      <div className="flex flex-row items-center gap-3 mt-4 mb-4">
        <div className="flex-1 min-w-0 rounded-full">
          <KnownLearningToggle
            value={viewMode}
            onChange={onViewModeChange}
            knownCount={knownCount}
            learningCount={learningCount}
          />
        </div>

        {/* Search toggle button */}
        <button
          aria-label="Search"
          onClick={handleSearchToggle}
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{
            backgroundColor: searchOpen
              ? "var(--color-grey-other)"
              : "var(--color-grey-lightest)",
          }}
        >
          <FaSearch
            className="w-[18px] h-[18px]"
            style={{ color: searchOpen ? "#fff" : "var(--color-grey)" }}
          />
        </button>

        {/* Add (+) button — only when an add handler is provided. */}
        {onAddPress && (
          <button
            aria-label="Add word"
            onClick={onAddPress}
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "var(--color-grey-other)" }}
          >
            <FaPlus className="w-[20px] h-[20px] text-white" />
          </button>
        )}
      </div>

      {/* Collapsible search bar — slides/fades in like iOS (250ms) */}
      <AnimatePresence initial={false}>
        {searchOpen && (
          <motion.div
            className="mb-4 overflow-hidden"
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              placeholderText={searchPlaceholder}
              autoFocus={searchOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SegmentedViewHeader;
